import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import { decode } from "js-base64";

/** API */
import NotificationApi, { HeirExpiredAlertItem } from "../../api/notification/notification.api";

/** UTILS */
import { colors } from "../../constants/colors";
import { getProfile } from "../../utils/app.utils";
import { dateToView } from "../../utils/date.utils";

/** ใช้ sessionStorage: แสดงครั้งเดียวต่อ session (แท็บ/เบราว์เซอร์ใหม่ หรือ login ใหม่จะแสดงอีก — logout() ลบ key นี้ทิ้ง) */
export const HEIR_ALERT_SHOWN_KEY_PREFIX = "HEIR_ALERT_SHOWN_";

const CONTACT_PHONE = "0-2279-2080-9";

/**
 * popup แจ้งเตือนผู้รับมรดกชั่วคราวหมดอายุ — เฉพาะลูกค้า (roleId 4)
 * แสดงกับตัวผู้รับมรดกเอง (user ที่ login เป็นผู้รับมรดกให้ผู้ถือสิทธิคนไหนแล้วหมดอายุ) ไม่ใช่ฝั่งผู้ถือสิทธิ
 * mount ใน AppShell (มี token + branch แล้ว) ไม่ใช่ SessionGuard เพราะตัวนั้น render ในหน้า login ด้วย
 * ใช้ MUI Dialog ไม่ใช่ ModalCustom เพราะ ModalCustom ผูก redux isShow ร่วมกับ modal อื่นในหน้า home
 */
export default function HeirExpiredAlert() {
  const profile: any = useMemo(() => {
    try {
      return JSON.parse(decode(JSON.parse(getProfile() || "{}")));
    } catch {
      return {};
    }
  }, []);
  /** เฉพาะลูกค้า (roleId 4) — Number() กันกรณี roleId มาเป็น string */
  const isCustomer = Number(profile?.roleId) === 4;
  // console.log(isCustomer,'isCustomer')
  // console.log(profile?.userId,'profile?.userId')

  const [open, setOpen] = useState<boolean>(false);
  const [items, setItems] = useState<HeirExpiredAlertItem[]>([]);

  useEffect(() => {
    if (!isCustomer || !profile?.userId) return;
    const storageKey = `${HEIR_ALERT_SHOWN_KEY_PREFIX}${profile.userId}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch {
      /* storage ถูกบล็อก — แสดงได้ตามปกติ */
    }

    let cancelled = false;
    NotificationApi.alert()
      .then((payload) => {
        const heir = payload.customer_heir;
        if (cancelled || !heir || heir.count <= 0) return;
        /** set ทันทีที่แสดง กันเด้งซ้ำตอน reload ระหว่าง login -> select-branch -> home */
        try {
          sessionStorage.setItem(storageKey, "1");
          console.log("HeirExpiredAlert: set sessionStorage", storageKey);
        } catch {
          /* ignore */
        }
        setItems(heir.items || []);
        setOpen(true);
      })
      .catch(() => {
        /* แจ้งเตือนไม่ควรบล็อกการใช้งาน */
      });
    return () => {
      cancelled = true;
    };
  }, [profile, isCustomer]);

  const handleClose = () => setOpen(false);

  /** role อื่นไม่ render Dialog เลย ไม่ใช่แค่ปิดไว้ */
  if (!isCustomer) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <IconButton onClick={handleClose} aria-label="ปิด" sx={{ position: "absolute", right: 8, top: 8, color: colors.textLightGray }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 4, px: { xs: 2, sm: 4 } }}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1.5}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "#FFF4E5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 44, color: "#ED6C02" }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 600, color: colors.themeMainColor, lineHeight: 1.4 }}>
            แจ้งเตือน: ระยะเวลาสิทธิ์ผู้รับมรดกชั่วคราว (30 วัน) ของคุณสิ้นสุดลงแล้ว
          </Typography>
          <Typography sx={{ fontSize: 16 }}>
            กรุณาจัดส่งเอกสารทางกฎหมายเพิ่มเติมเพื่อดำเนินการปรับสถานะเป็นผู้รับมรดกอย่างเป็นทางการ
          </Typography>
        </Box>

        {items.length > 0 && (
          <Box sx={{ mt: 2.5, border: `1px solid ${colors.borderInput}`, borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, backgroundColor: colors.bgBase, fontSize: 14, fontWeight: 600 }}>
              สิทธิผู้รับมรดกที่หมดอายุ {items.length} รายการ
            </Box>
            {items.map((item, index) => (
              <Box
                key={item.id_customer_agent}
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ px: 2, py: 1.25, borderTop: index === 0 ? "none" : `1px solid ${colors.borderInput}` }}
              >
                <Typography sx={{ fontSize: 16 }}>สิทธิผู้รับมรดกของ {item.owner_fullname || "-"}</Typography>
                <Typography sx={{ fontSize: 14, color: colors.red }}>
                  หมดอายุเมื่อ {item.heir_expire_date ? dateToView(item.heir_expire_date) : "-"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, backgroundColor: colors.bgBase }}>
          <Typography sx={{ fontSize: 14, color: colors.textLightGray, mb: 0.5 }}>
            หากมีข้อสงสัยหรือต้องการความช่วยเหลือ กรุณาติดต่อเจ้าหน้าที่
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>องค์การตลาดเพื่อเกษตรกร</Typography>
          <Typography sx={{ fontSize: 14 }}>
            เลขที่ 101 ถนนย่านพหลโยธิน แขวงจตุจักร เขตจตุจักร กรุงเทพฯ 10900
          </Typography>
          <Box
            component="a"
            href="tel:022792080"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, fontSize: 16, color: colors.themeMainColor, textDecoration: "none" }}
          >
            <PhoneRoundedIcon sx={{ fontSize: 18 }} /> โทร. {CONTACT_PHONE}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3, px: { xs: 2, sm: 4 } }}>
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            minWidth: 160,
            fontSize: 16,
            boxShadow: "none",
            backgroundColor: colors.themeMainColor,
            "&:hover": { backgroundColor: colors.themeMainColor, opacity: 0.9, boxShadow: "none" },
          }}
        >
          รับทราบ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
